import { useInvoiceHistory } from "../hooks/use-invoices";
import type { InvoiceHistoryEntry } from "../types/invoice.types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, FileEdit, Send, CheckCircle, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDateFormat } from "@/hooks/use-date-format";

interface InvoiceHistoryProps {
  invoiceId: string;
}

export function InvoiceHistory({ invoiceId }: InvoiceHistoryProps) {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const { data: history, isLoading } = useInvoiceHistory(invoiceId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('invoices.history.title')}</CardTitle>
          <CardDescription>{t('invoices.history.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('invoices.history.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('invoices.history.title')}</CardTitle>
          <CardDescription>{t('invoices.history.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('invoices.history.noHistory')}</p>
        </CardContent>
      </Card>
    );
  }

  const getIcon = (action: string) => {
    switch (action) {
      case "created":
        return <Plus className="h-4 w-4 text-green-600" />;
      case "updated":
        return <FileEdit className="h-4 w-4 text-blue-600" />;
      case "status_changed":
        return <Send className="h-4 w-4 text-orange-600" />;
      case "sent":
        return <Send className="h-4 w-4 text-orange-600" />;
      case "paid":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "date_changed":
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActionLabel = (entry: InvoiceHistoryEntry) => {
    switch (entry.action) {
      case "created":
        return t('invoices.history.created');
      case "updated":
        return t('invoices.history.updated');
      case "status_changed":
        if (entry.oldStatus && entry.newStatus) {
          const oldStatus = t(`invoices.statusLabels.${entry.oldStatus}`);
          const newStatus = t(`invoices.statusLabels.${entry.newStatus}`);
          return `${t('invoices.history.statusChanged')}: ${oldStatus} → ${newStatus}`;
        }
        return t('invoices.history.statusChanged');
      case "sent":
        return t('invoices.history.sent');
      case "paid":
        return t('invoices.history.paid');
      case "date_changed":
        // Use actionKey for translation with data
        if (entry.actionKey && entry.actionData) {
          try {
            const actionData = JSON.parse(entry.actionData);
            return t(entry.actionKey, actionData) as string;
          } catch (error) {
            console.error("Failed to parse actionData:", error);
            return t('invoices.history.updated');
          }
        }
        return t('invoices.history.updated');
      default:
        return entry.action;
    }
  };

  const getDescription = (entry: InvoiceHistoryEntry): string | null => {
    // For date_changed, description is already in the action label
    if (entry.action === 'date_changed') {
      return null;
    }
    
    // Use actionKey system for other actions
    if (entry.actionKey) {
      try {
        const actionData = entry.actionData ? JSON.parse(entry.actionData) : {};
        return t(entry.actionKey, actionData) as string;
      } catch (error) {
        console.error("Failed to parse actionData:", error);
        return null;
      }
    }
    
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invoices.history.title')}</CardTitle>
        <CardDescription>{t('invoices.history.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative space-y-4 max-h-[500px] overflow-y-auto pr-2">
          {/* Timeline Line */}
          <div className="absolute left-[15px] top-2 bottom-2 w-[2px] bg-border" />

          {history.map((entry) => (
            <div key={entry.id} className="relative flex gap-4">
              {/* Icon Circle */}
              <div className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-background border-2 border-border">
                {getIcon(entry.action)}
              </div>

              {/* Content */}
              <div className="flex-1 space-y-1 pt-0.5">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium leading-none">
                    {getActionLabel(entry)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(new Date(entry.createdAt))}
                  </p>
                </div>
                
                {entry.user && (
                  <p className="text-xs text-muted-foreground">
                    {t('invoices.history.by')} {entry.user.name}
                  </p>
                )}

                {entry.actionKey && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {getDescription(entry)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
